import { ChangeDetectionStrategy, Component, Type, input, output } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';

export interface BottomNavItem {
  id: string;
  label: string;
  icon: Type<unknown>;
}

/** Mobile primary navigation — max 5 items, icon always paired with a label. */
@Component({
  selector: 'ds-bottom-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    <nav class="nav" [attr.aria-label]="ariaLabel()">
      @for (item of items(); track item.id) {
        <button
          type="button"
          class="nav__item"
          [class.nav__item--active]="item.id === activeId()"
          [attr.aria-current]="item.id === activeId() ? 'page' : null"
          (click)="itemSelected.emit(item.id)"
        >
          <span class="nav__icon" aria-hidden="true">
            <ng-container
              [ngComponentOutlet]="item.icon"
              [ngComponentOutletInputs]="{ size: 26, strokeWidth: 2 }"
            />
          </span>
          <span class="nav__label">{{ item.label }}</span>
        </button>
      }
    </nav>
  `,
  styles: `
    :host {
      display: block;
    }

    .nav {
      display: flex;
      background: var(--color-surface);
      border-top: 2px solid var(--color-border-subtle);
      padding-bottom: env(safe-area-inset-bottom, 0);
    }

    .nav__item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-1);
      min-height: 68px;
      padding: var(--space-2) var(--space-1);
      border: 0;
      border-top: 4px solid transparent;
      background: transparent;
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: color var(--motion-fast), border-color var(--motion-fast);

      &:hover {
        color: var(--color-text);
      }

      &--active {
        color: var(--color-primary);
        border-top-color: var(--color-primary);
        font-weight: var(--font-weight-bold);
      }
    }

    .nav__icon {
      display: inline-flex;
    }

    .nav__label {
      font-size: var(--text-sm);
      line-height: 1.2;
    }
  `,
})
export class BottomNavComponent {
  items = input.required<BottomNavItem[]>();
  activeId = input.required<string>();
  ariaLabel = input('Navigasi utama');
  itemSelected = output<string>();
}
